import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getDataAreaId } from "../../services/dataAreaService";

const FLOW_BPM_UI_BASE = process.env.REACT_APP_FLOW_BPM_UI_BASE || "";

const resolveTenantId = () =>
  localStorage.getItem("tenantId")
  || localStorage.getItem("selectedTenant")
  || localStorage.getItem("selectedTenantId")
  || "";

const FlowBpmUiHost = ({ entry = "/workflow/", title = "iDynamicsAX Flow BPM UI" }) => {
  const { authToken } = useAuth();
  const iframeRef = useRef(null);
  const [tenantId, setTenantId] = useState(resolveTenantId);
  const [dataAreaId, setDataAreaId] = useState(() => getDataAreaId() || "");
  const [themeVersion, setThemeVersion] = useState(0);

  const base = FLOW_BPM_UI_BASE.replace(/\/$/, "");
  const frameSrc = useMemo(() => {
    if (!authToken) {
      return `${base}${entry}`;
    }

    const params = new URLSearchParams();
    if (tenantId) {
      params.set("tenant", tenantId);
    }
    if (dataAreaId) {
      params.set("dataArea", dataAreaId);
    }
    params.set("redirect", entry);

    return `${base}/auto-login/${encodeURIComponent(authToken)}?${params.toString()}`;
  }, [authToken, base, dataAreaId, entry, tenantId]);

  const injectThemeStyles = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc?.head) {
        return;
      }

      const params = new URLSearchParams();
      if (tenantId) {
        params.set("tenant", tenantId);
      }
      if (dataAreaId) {
        params.set("dataArea", dataAreaId);
      }
      params.set("v", String(themeVersion));

      const href = `${base}/flow-bpm/theme.css?${params.toString()}`;
      let link = doc.getElementById("idax-flow-bpm-theme");
      if (!link) {
        link = doc.createElement("link");
        link.id = "idax-flow-bpm-theme";
        link.rel = "stylesheet";
        doc.head.appendChild(link);
      }
      if (link.href !== href) {
        link.href = href;
      }
    } catch {
      // The default proxy keeps the iframe same-origin. If a custom cross-origin
      // target is configured, the legacy UI will simply keep its bundled styles.
    }
  }, [base, dataAreaId, tenantId, themeVersion]);

  useEffect(() => {
    const handleDataAreaChanged = (event) => {
      setTenantId(resolveTenantId());
      setDataAreaId(event?.detail?.dataAreaId || getDataAreaId() || "");
      setThemeVersion((value) => value + 1);
    };

    const handleThemeUpdated = () => {
      setTenantId(resolveTenantId());
      setDataAreaId(getDataAreaId() || "");
      setThemeVersion((value) => value + 1);
    };

    window.addEventListener("dataAreaChanged", handleDataAreaChanged);
    window.addEventListener("themeUpdated", handleThemeUpdated);
    return () => {
      window.removeEventListener("dataAreaChanged", handleDataAreaChanged);
      window.removeEventListener("themeUpdated", handleThemeUpdated);
    };
  }, []);

  useEffect(() => {
    injectThemeStyles();
  }, [injectThemeStyles]);

  return (
    <iframe
      ref={iframeRef}
      title={title}
      src={frameSrc}
      onLoad={injectThemeStyles}
      style={{
        border: 0,
        width: "100%",
        height: "calc(100vh - 135px)",
        minHeight: 720,
        background: "#fff",
      }}
    />
  );
};

export default FlowBpmUiHost;
